import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { CacheService, CacheEntry } from './cache.service';
import {
  CustomerFeedbackResponse,
  CustomerFeedbackApiResponse,
  CustomerFeedbackItem,
} from '../type/customer-ratings_type';

@Injectable({
  providedIn: 'root',
})
export class CustomerFeedbackService {
  private feedbackCache = new Map<string, CacheEntry<CustomerFeedbackResponse>>();

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'api-key': 'check24at',
      'x-from-service': 'frontend-resultpage',
    }),
  };

  constructor(
    private http: HttpClient,
    private cacheService: CacheService
  ) {}

  /**
   * Get customer feedback for a product with pagination
   * @param productId - The product ID
   * @param pageSize - Number of items per page (default 4)
   * @param cursor - Pagination cursor
   * @param sortingEnum - Sort order enum
   * @param retrieveTestData - Whether to retrieve test data (default false)
   * @returns Observable of CustomerFeedbackResponse
   */
  getCustomerFeedback(
    productId: string,
    pageSize: number = 4,
    cursor: string = '',
    sortingEnum: string = 'HIGHEST',
    retrieveTestData: boolean = false
  ): Observable<CustomerFeedbackResponse | null> {
    const cacheKey = `${productId}_${pageSize}_${cursor}_${sortingEnum}_${retrieveTestData}`;

    // Return cached feedback if valid
    if (this.cacheService.isValid(this.feedbackCache, cacheKey)) {
      return of(this.cacheService.get(this.feedbackCache, cacheKey)!);
    }

    const url = `/api/customerratings/${encodeURIComponent(productId)}/page`;
    let params = new HttpParams()
      .set('pageSize', pageSize.toString())
      .set('sortingEnum', sortingEnum)
      .set('retrieveTestData', retrieveTestData.toString());

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http
      .get<CustomerFeedbackApiResponse>(url, {
        params,
        ...this.httpOptions,
      })
      .pipe(
        map((response) => this.transformFeedbackResponse(response)),
        tap((data) => {
          if (data) {
            this.cacheService.set(this.feedbackCache, cacheKey, data);
          }
        }),
        catchError((error) => {
          console.error('Error fetching customer feedback:', error);
          return of(null);
        })
      );
  }

  /**
   * Transform API response to CustomerFeedbackResponse
   */
  private transformFeedbackResponse(
    response: CustomerFeedbackApiResponse
  ): CustomerFeedbackResponse | null {
    if (!response?.data) {
      return null;
    }

    const feedbackItems: CustomerFeedbackItem[] = response.data.map((item) => ({
      rating: item.rate,
      comment: item.comment || '',
      authorName: item.name || 'Anonym',
      date: item.created_at,
    }));

    return {
      feedbackItems,
      nextCursor: response.nextCursor || '',
      prevCursor: response.prevCursor || '',
      hasNext: response.hasNext || false,
      hasPrevious: response.hasPrevious || false,
    };
  }
}
